/* Topptekst: skuff, søk og skrivebordspaneler.

   Skuffen og panelene virker uten JS der det er mulig (<details>, vanlige
   lenker). Dette laget legger til det nettleseren ikke gir gratis:

   - Skuffen og fullskjermsøket er modale: Tab sykler inni dem, Escape lukker,
     bakgrunnen er inert og kan ikke rulles, fokus går tilbake dit det var.
   - Kategoripanelene på skrivebord åpnes av pila, ved hover (med forsinkelse,
     så pekeren kan krysse hullet mellom to punkter) og med tastatur.
   - Kategoriraden glir bort ved scroll nedover og kommer tilbake ved scroll
     oppover. Høyden som er synlig, legges i --gs-header-h, og ett sted
     (html { scroll-padding-top }) bruker den til å la ankerhopp treffe.
   - Fanger endring av skjermbredde: en åpen skuff på et nettbrett som dreies
     til liggende skal ikke etterlate en inert, ulåst side.
   - Rydder opp når siden hentes tilbake fra tilbake-knappen (bfcache) og når
     temaeditoren bytter ut seksjonen. */

const BRYTEPUNKT = '(min-width: 1100px)';
const ER_TASTATUR_EL =
  'a[href], button:not([disabled]), summary, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

class GsHeader extends HTMLElement {
  connectedCallback() {
    if (this.klar) return;
    this.klar = true;
    this.lyttere = [];

    this.skuff = this.querySelector('[data-gs-drawer]');
    this.panel = this.skuff ? this.skuff.querySelector('.gs-drawer__panel') : null;
    this.sok = this.querySelector('[data-gs-sok]');
    this.sokInput = this.sok ? this.sok.querySelector('input[type="search"]') : null;
    this.bar = this.querySelector('.gs-header__bar');
    this.nav = this.querySelector('.gs-nav');
    this.grupper = [...this.querySelectorAll('[data-gs-gruppe]')];
    this.stor = window.matchMedia(BRYTEPUNKT);
    this.sokApen = false;
    this.satteInert = [];

    this.startSkuff();
    this.startSok();
    this.startPaneler();
    this.startHoyde();
    this.skjulUtgaatte();

    this.lytt(document, 'keydown', (e) => this.vedTast(e));
    this.lytt(document, 'click', (e) => {
      if (!this.contains(e.target)) this.lukkPaneler();
    });

    // Skuff og fullskjermsøk finnes bare under 1100 px.
    this.lytt(this.stor, 'change', () => {
      if (this.stor.matches) {
        if (this.skuff && this.skuff.open) this.lukkSkuff(false);
        if (this.sokApen) this.lukkSok(false);
      } else {
        this.lukkPaneler();
        this.settKompakt(false);
      }
      this.settHoyde();
    });

    // Tilbake-knappen kan gi en side som sto med skuffen eller søket åpent.
    this.lytt(window, 'pageshow', (e) => {
      if (!e.persisted) return;
      if (this.skuff && this.skuff.open) this.lukkSkuff(false);
      if (this.sokApen) this.lukkSok(false);
      this.lukkPaneler();
      this.oppdaterLas();
    });
  }

  disconnectedCallback() {
    // Temaeditoren bytter ut seksjonen når noe endres. Da skal ikke de gamle
    // lytterne bli hengende på document og window.
    (this.lyttere || []).forEach(([mal, type, fn, valg]) => mal.removeEventListener(type, fn, valg));
    this.lyttere = [];
    if (this.ro) this.ro.disconnect();
    this.sokApen = false;
    this.satteInert.forEach((el) => el.removeAttribute('inert'));
    this.satteInert = [];
    document.body.style.overflow = '';
    this.klar = false;
  }

  /* Lytter som fjernes igjen i disconnectedCallback. */
  lytt(mal, type, fn, valg) {
    mal.addEventListener(type, fn, valg);
    this.lyttere.push([mal, type, fn, valg]);
  }

  /* ------------------------------------------------- utgåtte kunngjøringer */

  /* Serveren skjuler en kunngjøring etter sluttdatoen (sections/announcement-
     bar.liquid). Ligger en ferdig rendret side mellomlagret over midnatt,
     gjør denne kontrollen det samme i nettleseren: heltallet ÅÅÅÅMMDD i
     data-slutt mot dagens dato i Oslo. Samme regel som forsidekampanjen.
     Ikke i temaeditoren, der utgåtte kunngjøringer vises nedtonet med vilje. */
  skjulUtgaatte() {
    if (window.Shopify && window.Shopify.designMode) return;
    const punkter = document.querySelectorAll('.gs-ann__punkt[data-slutt]');
    if (!punkter.length) return;
    let idag = 0;
    try {
      idag = parseInt(new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' }).replace(/-/g, ''), 10);
    } catch (_) {
      return;
    }
    if (!idag) return;
    punkter.forEach((el) => {
      const slutt = parseInt(el.getAttribute('data-slutt'), 10);
      if (slutt > 0 && idag > slutt) el.hidden = true;
    });
  }

  /* ---------------------------------------------------------------- lås */

  /* Bakgrunnen er låst mens skuffen eller søket er åpent. Regnes ut fra
     tilstanden hver gang, så skuffen kan lukkes mens søket åpnes uten at det
     ene låser opp det andre. */
  oppdaterLas() {
    const apen = (this.skuff && this.skuff.open) || this.sokApen;
    document.body.style.overflow = apen ? 'hidden' : '';

    this.satteInert.forEach((el) => el.removeAttribute('inert'));
    this.satteInert = [];
    if (!apen) return;
    // Alt utenfor toppteksten tas ut av tabrekkefølgen og skjules for
    // skjermlesere. Bare det vi selv har satt, fjernes igjen.
    [...document.body.children].forEach((el) => {
      if (el.contains(this) || el.hasAttribute('inert')) return;
      el.setAttribute('inert', '');
      this.satteInert.push(el);
    });
  }

  fokuserbare(beholder) {
    if (!beholder) return [];
    // Innholdet i en lukket <details> er skjult med content-visibility, ikke
    // display: none, i nyere nettlesere. offsetParent sier da «synlig». Derfor
    // checkVisibility, så ikke en lenke i en lukket gruppe blir «siste».
    const synlig = (el) => {
      if (el === document.activeElement) return true;
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ contentVisibilityAuto: true, visibilityProperty: true });
      }
      return el.offsetParent !== null;
    };
    // Forslagene i søket har tabindex -1: de nås med piltastene, ikke med Tab.
    return [...beholder.querySelectorAll(ER_TASTATUR_EL)].filter((el) => el.tabIndex !== -1 && synlig(el));
  }

  fangFokus(e, beholder) {
    if (e.key !== 'Tab') return;
    const felt = this.fokuserbare(beholder);
    if (!felt.length) return;
    const forste = felt[0];
    const siste = felt[felt.length - 1];
    const inni = beholder.contains(document.activeElement);
    if (e.shiftKey && (document.activeElement === forste || !inni)) {
      e.preventDefault();
      siste.focus();
    } else if (!e.shiftKey && (document.activeElement === siste || !inni)) {
      e.preventDefault();
      forste.focus();
    }
  }

  vedTast(e) {
    if (e.key === 'Escape') {
      if (this.sokApen) {
        this.lukkSok(true);
      } else if (this.skuff && this.skuff.open) {
        this.lukkSkuff(true);
      } else if (this.sok && this.sok.contains(document.activeElement)) {
        this.lukkSokBoks(e);
      } else {
        const apen = this.grupper.find((g) => g.classList.contains('er-apen'));
        if (apen) {
          const fokusInni = apen.contains(document.activeElement);
          this.lukkPaneler();
          if (fokusInni) apen.querySelector('.gs-nav__bryter').focus();
        }
      }
      return;
    }
    if (e.key === 'Tab') {
      if (this.sokApen) this.fangFokus(e, this.sok);
      else if (this.skuff && this.skuff.open) this.fangFokus(e, this.panel);
    }
  }

  /* Lenke til et anker på samme side: overlegget skal lukkes, ellers blir det
     stående over stedet man nettopp hoppet til. */
  erAnkerPaaSammeSide(a) {
    try {
      const url = new URL(a.href, location.href);
      return url.pathname === location.pathname && url.hash.length > 1;
    } catch (_) {
      return false;
    }
  }

  /* -------------------------------------------------------------- skuff */

  startSkuff() {
    if (!this.skuff) return;
    this.querySelectorAll('[data-gs-lukk]').forEach((el) =>
      this.lytt(el, 'click', (e) => {
        e.preventDefault();
        this.lukkSkuff(true);
      })
    );
    this.lytt(this.skuff, 'toggle', () => {
      if (this.skuff.open) this.apneSkuff();
      else this.oppdaterLas();
    });
    this.lytt(this.panel, 'click', (e) => {
      const a = e.target.closest('a[href]');
      if (a && !a.hasAttribute('data-gs-sok-apne') && this.erAnkerPaaSammeSide(a)) this.lukkSkuff(false);
    });
  }

  apneSkuff() {
    this.oppdaterLas();
    const forste = this.fokuserbare(this.panel)[0];
    if (forste) forste.focus();
  }

  lukkSkuff(tilbakeFokus) {
    if (!this.skuff) return;
    this.skuff.open = false;
    this.oppdaterLas();
    const bryter = this.skuff.querySelector('summary');
    if (tilbakeFokus && bryter) bryter.focus();
  }

  /* ---------------------------------------------------------------- søk */

  startSok() {
    if (!this.sok) return;
    this.apnere = [...this.querySelectorAll('[data-gs-sok-apne]')];
    this.apnere.forEach((el) =>
      this.lytt(el, 'click', (e) => {
        e.preventDefault();
        this.apneSok(el);
      })
    );
    this.querySelectorAll('[data-gs-sok-lukk]').forEach((el) =>
      this.lytt(el, 'click', () => this.lukkSok(true))
    );
    this.lytt(this.sok, 'click', (e) => {
      const a = e.target.closest('a[href]');
      if (a && this.sokApen && this.erAnkerPaaSammeSide(a)) this.lukkSok(false);
    });

    // Etter Escape holdes snarveiene og forslagene lukket (data-lukket) til
    // brukeren selv ber om dem igjen: et trykk på feltet, pil ned, eller at
    // fokus har vært borte og kommer tilbake.
    const apneIgjen = () => this.sok.removeAttribute('data-lukket');
    if (this.sokInput) {
      this.lytt(this.sokInput, 'pointerdown', apneIgjen);
      this.lytt(this.sokInput, 'input', apneIgjen);
    }
    this.lytt(this.sok, 'keydown', (e) => {
      if (e.key === 'ArrowDown') apneIgjen();
    });
    this.lytt(this.sok, 'focusout', (e) => {
      if (!this.sok.contains(e.relatedTarget)) apneIgjen();
    });
  }

  /* Escape i søkefeltet på skrivebord. Første trykk lukker det som henger
     under feltet, snarveiene når feltet er tomt eller forslagene når det er
     skrevet noe, og lar det som er skrevet stå. Uten preventDefault tømmer
     Chrome et søkefelt ved Escape, og snarveiene kommer rett tilbake. Er
     ingenting åpent, gjør nettleseren det den pleier (tømmer feltet).
     Snarveiene lå over kategoriraden og kunne ikke fjernes uten å flytte
     fokus (WCAG 1.4.13). */
  lukkSokBoks(e) {
    const tom = this.sok.querySelector('.gs-sok__tom');
    const snarveierSynlig = !!tom && tom.getClientRects().length > 0;
    const forslagApne = this.sok.hasAttribute('open');
    if (!snarveierSynlig && !forslagApne) return;
    e.preventDefault();
    this.sok.setAttribute('data-lukket', '');
    if (typeof this.sok.close === 'function') this.sok.close();
    // Sto fokus på en snarvei som nå skjules, tilbake til feltet.
    if (this.sokInput && document.activeElement !== this.sokInput) this.sokInput.focus();
  }

  apneSok(fra) {
    // Fra 1100 px er feltet alltid synlig, og det er bare å skrive i det.
    if (this.stor.matches) {
      if (this.sokInput) this.sokInput.focus();
      return;
    }
    // Åpnet fra skuffen: skuffen lukkes, og fokus går til søkeknappen i
    // toppen når søket lukkes igjen.
    const fraSkuff = fra && fra.closest('[data-gs-drawer]');
    this.sokFra = fraSkuff ? this.querySelector('.gs-header__sok') : fra;
    if (this.skuff && this.skuff.open) this.skuff.open = false;
    this.sokApen = true;
    this.sok.classList.add('gs-sok--apen');
    this.apnere.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    this.oppdaterLas();
    if (this.sokInput) {
      this.sokInput.focus();
      // Markøren sist, ikke først, når siden åpnes med et søk fra før.
      const n = this.sokInput.value.length;
      try {
        this.sokInput.setSelectionRange(n, n);
      } catch (_) {
        /* type=search godtar det ikke overalt */
      }
    }
  }

  lukkSok(tilbakeFokus) {
    if (!this.sok) return;
    this.sokApen = false;
    this.sok.classList.remove('gs-sok--apen');
    this.apnere.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (typeof this.sok.close === 'function') this.sok.close();
    this.oppdaterLas();
    if (tilbakeFokus && this.sokFra && this.sokFra.offsetParent !== null) this.sokFra.focus();
  }

  /* ------------------------------------------------------------ paneler */

  startPaneler() {
    const mus = (e) => e.pointerType === 'mouse' && this.stor.matches;

    this.grupper.forEach((g) => {
      const bryter = g.querySelector('.gs-nav__bryter');
      if (!bryter) return;

      // Et panel som hover åpnet, skal ikke lukkes av et trykk på pila: den som
      // peker på pila og trykker, vil ha panelet, ikke miste det. Trykket
      // fester det i stedet, til man trykker utenfor eller på Escape.
      this.lytt(bryter, 'click', (e) => {
        if (g.classList.contains('er-apen')) {
          if (g.viaHover && e.detail > 0) {
            g.viaHover = false;
            return;
          }
          this.lukkPanel(g);
        } else {
          g.viaHover = false;
          this.apnePanel(g);
        }
      });

      // Hover-intent: litt forsinkelse inn, litt mer ut. Uten den åpnes et
      // panel av at pekeren bare farer forbi på vei mot noe annet, og lukkes
      // i det den skrår over kanten.
      this.lytt(g, 'pointerenter', (e) => {
        if (!mus(e)) return;
        clearTimeout(g.timer);
        if (g.classList.contains('er-apen')) return;
        g.timer = setTimeout(() => {
          g.viaHover = true;
          this.apnePanel(g);
        }, 120);
      });
      this.lytt(g, 'pointerleave', (e) => {
        if (!mus(e)) return;
        clearTimeout(g.timer);
        // Festet med et trykk: blir stående.
        if (g.classList.contains('er-apen') && !g.viaHover) return;
        g.timer = setTimeout(() => this.lukkPanel(g), 250);
      });

      // Fokus ut av punktet lukker panelet.
      this.lytt(g, 'focusout', (e) => {
        if (!g.contains(e.relatedTarget)) this.lukkPanel(g);
      });
    });
  }

  apnePanel(g) {
    this.grupper.forEach((annen) => {
      if (annen !== g) this.lukkPanel(annen);
    });
    clearTimeout(g.timer);
    g.classList.add('er-apen');
    const b = g.querySelector('.gs-nav__bryter');
    if (b) b.setAttribute('aria-expanded', 'true');
    // Et åpent panel henger under raden, så raden skal ikke gli bort.
    this.settKompakt(false);
  }

  lukkPanel(g) {
    clearTimeout(g.timer);
    if (!g.classList.contains('er-apen')) return;
    g.classList.remove('er-apen');
    g.viaHover = false;
    const b = g.querySelector('.gs-nav__bryter');
    if (b) b.setAttribute('aria-expanded', 'false');
  }

  lukkPaneler() {
    this.grupper.forEach((g) => this.lukkPanel(g));
  }

  panelApent() {
    return this.grupper.some((g) => g.classList.contains('er-apen'));
  }

  /* ------------------------------------------------------------- høyde */

  startHoyde() {
    this.sisteY = window.scrollY;
    this.sum = 0;
    this.frosset = false;
    this.classList.toggle('er-scrollet', window.scrollY > 8);

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.settHoyde());
      this.ro.observe(this.bar);
      if (this.nav) this.ro.observe(this.nav);
    }
    this.lytt(window, 'resize', () => this.settHoyde(), { passive: true });
    this.settHoyde();

    this.lytt(
      window,
      'scroll',
      () => {
        if (this.tikker) return;
        this.tikker = true;
        requestAnimationFrame(() => {
          this.tikker = false;
          this.vedScroll();
        });
      },
      { passive: true }
    );

    // Tastaturet er i toppteksten: raden må være synlig, ellers er lenkene
    // skjult og kan ikke nås.
    this.lytt(this, 'focusin', () => this.settKompakt(false));

    // Ankerhopp: scroll-padding regnes med at kategoriraden er synlig, så målet
    // treffer rett under den. Raden skal derfor stå til brukeren selv ruller.
    // Den var før frosset i ett sekund; så gled den bort uten at siden flyttet
    // seg, og et tomt bånd på 61 px sto igjen mellom toppteksten og målet
    // (målt på 1440 px etter hopp til #bestill-time). Tidsfristen var bare en
    // gjetning på når hoppet var ferdig, og scrollhendelser som ikke er
    // brukerens (jevn rulling underveis, bilder som laster og flytter siden)
    // kom også etter den.
    this.lytt(document, 'click', (e) => {
      const a = e.target.closest && e.target.closest('a[href*="#"]');
      if (a && this.erAnkerPaaSammeSide(a)) this.frys();
    });
    this.lytt(window, 'hashchange', () => this.frys());
    if (location.hash.length > 1) this.frys();

    // Det som tiner raden igjen: hjul, berøring, rulletaster og trykk på
    // selve rullefeltet (målet er da html-elementet).
    const SCROLLTASTER = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '];
    const tin = () => this.tin();
    this.lytt(window, 'wheel', tin, { passive: true });
    this.lytt(window, 'touchmove', tin, { passive: true });
    this.lytt(document, 'pointerdown', (e) => {
      if (e.target === document.documentElement) this.tin();
    });
    this.lytt(document, 'keydown', (e) => {
      if (!SCROLLTASTER.includes(e.key)) return;
      const t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
      this.tin();
    });
  }

  /* Raden holdes som den er til brukeren ruller. */
  frys() {
    this.frosset = true;
  }

  tin() {
    if (!this.frosset) return;
    this.frosset = false;
    // sisteY røres ikke: vedScroll holder den oppdatert også mens raden er
    // frosset, og hjulhendelsen kommer ofte etter at siden har begynt å
    // flytte seg. Å lese scrollY her ville spist den første bevegelsen.
    this.sum = 0;
  }

  vedScroll() {
    const y = Math.max(0, window.scrollY);
    const dy = y - this.sisteY;
    this.sisteY = y;

    // En svak skygge under toppteksten så snart noe ruller under den.
    this.classList.toggle('er-scrollet', y > 8);

    if (!this.stor.matches || this.frosset) return;

    // Øverst på siden er raden alltid der.
    if (y < 120) {
      this.sum = 0;
      this.settKompakt(false);
      return;
    }
    // Åpne forslag eller et åpent panel: la raden stå.
    if (this.panelApent() || (this.sok && this.sok.contains(document.activeElement))) {
      this.sum = 0;
      return;
    }
    // Summér bevegelsen i samme retning. 24 px før den slår om, så et lite
    // rykk på styreflaten ikke får raden til å blinke.
    if (Math.sign(dy) !== Math.sign(this.sum)) this.sum = 0;
    this.sum += dy;
    if (this.sum > 24) this.settKompakt(true);
    else if (this.sum < -24) this.settKompakt(false);
  }

  settKompakt(kompakt) {
    if (!this.stor.matches) kompakt = false;
    if (this.classList.contains('er-kompakt') === kompakt) return;
    this.classList.toggle('er-kompakt', kompakt);
    if (kompakt) this.lukkPaneler();
    this.settHoyde();
  }

  /* Høyden som faktisk dekker toppen av vinduet akkurat nå. */
  settHoyde() {
    if (!this.bar) return;
    let h = this.bar.offsetHeight;
    const kompakt = this.classList.contains('er-kompakt');
    if (this.nav && this.stor.matches && !kompakt) h += this.nav.offsetHeight;
    document.documentElement.style.setProperty('--gs-header-h', `${h}px`);
  }
}

if (!customElements.get('gs-header')) customElements.define('gs-header', GsHeader);
